export type DetectedImageType = 'image/jpeg' | 'image/png' | 'image/webp'

const HEADER_BYTES = 12

const readHeader = (blob: Blob) =>
  new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = () => resolve(reader.result as ArrayBuffer)
    reader.onerror = () => reject(reader.error)
    reader.readAsArrayBuffer(blob)
  })

const startsWith = (bytes: Uint8Array, signature: number[], offset = 0) =>
  signature.every((byte, index) => bytes[offset + index] === byte)

/**
 * Identifies JPEG/PNG/WebP by magic bytes, ignoring the file name and the browser-reported
 * MIME type (both are user-controlled). Returns null for anything else, e.g. a CSV renamed `.png`.
 */
export const detectImageType = async (file: Blob): Promise<DetectedImageType | null> => {
  try {
    const bytes = new Uint8Array(await readHeader(file.slice(0, HEADER_BYTES)))

    if (startsWith(bytes, [0xff, 0xd8, 0xff])) return 'image/jpeg'
    if (startsWith(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return 'image/png'

    const isRiff = startsWith(bytes, [0x52, 0x49, 0x46, 0x46])
    const isWebp = startsWith(bytes, [0x57, 0x45, 0x42, 0x50], 8)

    return isRiff && isWebp ? 'image/webp' : null
  } catch {
    return null
  }
}
